import { useState } from 'react';
import { 
    Trash2, 
    Pen, 
    ChevronDown, 
    ChevronUp,
    Dumbbell,
    Clock,
    Hash,
    Weight,
    Repeat
} from 'lucide-react';

type ConditioningEntryProps = {
    entry: any;
    circuit: any;
    exerciseList: any[];
    setEditing: (entry: any) => void;
    remove: (id: string) => void;
};

const ConditioningEntry: React.FC<ConditioningEntryProps> = ({ entry, circuit, exerciseList, setEditing, remove }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleDeleteClick = () => {
        remove(entry.id);
    };

    if (entry.type === 'exercise') {
        const exName = exerciseList?.find(ex => (ex.id === entry.exercise_id && ex.type === entry.exercise_type))?.name;
        
        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-4 relative">
                    <div className="absolute inset-0 bg-white opacity-10"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Dumbbell className="w-4 h-4" />
                            <div>
                                <h3 className="font-bold text-lg text-black">
                                    Exercise
                                </h3>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setEditing(entry)}
                                className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                title="Edit"
                            >
                                <Pen className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                            </button>

                            <button
                                onClick={handleDeleteClick}
                                className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                title="Delete"
                            >
                                <Trash2 className="w-4 h-4 text-white" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="p-6">
                    {/* Exercise Name */}
                    <div className="mb-4">
                        <h4 className="text-xl font-semibold text-gray-800 leading-tight">
                            {exName}
                        </h4>
                    </div>

                    {/* Exercise Details */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                <Hash className="w-5 h-5 text-blue-500" />
                                <div>
                                    <div className="text-sm text-blue-600 font-medium">Reps</div>
                                    <div className="text-lg font-bold text-blue-800">
                                        {entry.reps}
                                    </div>
                                </div>
                            </div>

                            {entry.duration > 0 && (
                                <div className="bg-green-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Clock className="w-5 h-5 text-green-500" />
                                    <div>
                                        <div className="text-sm text-green-600 font-medium">Duration</div>
                                        <div className="text-lg font-bold text-green-800">
                                            {entry.duration}s
                                        </div>
                                    </div>
                                </div>
                            )}

                            {entry.weight > 0 && (
                                <div className="bg-purple-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Weight className="w-5 h-5 text-purple-500" />
                                    <div>
                                        <div className="text-sm text-purple-600 font-medium">Weight</div>
                                        <div className="text-lg font-bold text-purple-800">
                                            {entry.weight}kg
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4">
                {/* Header */}
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4 relative">
                    <div className="absolute inset-0 bg-white opacity-10"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Repeat className="w-4 h-4" />
                            <div>
                                <h3 className="font-bold text-lg">
                                    Circuit
                                </h3>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setEditing(entry)}
                                className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                title="Edit"
                            >
                                <Pen className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                            </button>

                            <button
                                onClick={handleDeleteClick}
                                className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                title="Delete"
                            >
                                <Trash2 className="w-4 h-4 text-white" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="p-6">
                    {/* Circuit Name */}
                    <div className="mb-4">
                        <h4 className="text-xl font-semibold text-gray-800 leading-tight">
                            {circuit?.title}
                        </h4>
                    </div>

                    {/* Quick Stats */}
                    {!isExpanded && (
                        <div className="grid grid-cols-1 gap-4 mb-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                                <div className="text-sm text-gray-500">Sets</div>
                                <div className="text-lg font-bold text-gray-800">
                                    {entry.sets}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Expanded Details */}
                    <div className={`transition-all duration-300 overflow-hidden ${
                        isExpanded ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                        <div className="space-y-4 pt-4 border-t border-gray-100">
                            {/* Sets Display */}
                            <div className="grid grid-cols-1 gap-4">
                                <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Repeat className="w-5 h-5 text-blue-500" />
                                    <div>
                                        <div className="text-sm text-blue-600 font-medium">Sets</div>
                                        <div className="text-lg font-bold text-blue-800">
                                            {entry.sets}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Exercises Table */}
                            <div className="bg-indigo-50 rounded-lg p-4">
                                <div className="flex items-center space-x-2 mb-3">
                                    <Dumbbell className="w-5 h-5 text-indigo-600" />
                                    <span className="font-semibold text-indigo-800 text-sm">
                                        Exercises ({circuit?.exercises?.length || 0})
                                    </span>
                                </div>
                                <div className="bg-white rounded-lg overflow-hidden border border-indigo-100">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-indigo-100">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-indigo-700 uppercase">Exercise</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-indigo-700 uppercase">Reps</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-indigo-700 uppercase">Duration</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-indigo-700 uppercase">Weight</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-indigo-50">
                                                {circuit?.exercises?.map((cEx: any, index: number) => {
                                                    const exercise = exerciseList.find(
                                                        ex => ex.id === cEx.exercise_id && ex.type === cEx.exercise_type
                                                    );
                                                    
                                                    return (
                                                        <tr key={index} className="hover:bg-indigo-25">
                                                            <td className="px-3 py-2 text-sm text-indigo-900">
                                                                {exercise ? exercise.name : "Unknown Exercise"}
                                                            </td>
                                                            <td className="px-3 py-2 text-sm text-indigo-800 font-medium text-center">
                                                                {cEx.reps || '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-sm text-indigo-800 font-medium text-center">
                                                                {cEx.duration ? `${cEx.duration}s` : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-sm text-indigo-800 font-medium text-center">
                                                                {cEx.weight ? `${cEx.weight}kg` : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Toggle Button */}
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 font-medium text-gray-700"
                    >
                        <span className="text-base">{isExpanded ? 'Hide Exercises' : 'Show Exercises'}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        );
    }
};

export default ConditioningEntry;